import { FiLink } from 'react-icons/fi';

export const linkingWordsBasicData = {
  courseId: 'linking-words-basic',
  courseType: 'vocabulary',
  title: 'Basic Linking Words',
  icon: FiLink,
  overview: {
    impact: 'Connect ideas clearly using simple linking words — the foundation for IELTS coherence',
    focus: 'Essential linkers (and, but, so, because, or, then, also, first/next/finally), compound sentences, and avoiding common mistakes',
    uses: 'Writing coherence (all tasks), Speaking fluency, Building blocks for advanced connectors',
    duration: '20-25 minutes'
  },

  sections: [
    {
      title: 'What Are Linking Words?',
      content: `<p><strong>Linking words</strong> (also called <em>conjunctions</em> or <em>connectors</em>) are small words that join ideas together. Without them, your writing and speaking sound like a list of disconnected sentences.</p>
<p><strong>Without linking words:</strong> <em>I studied hard. I passed the exam.</em> (choppy, unclear relationship)</p>
<p><strong>With a linking word:</strong> <em>I studied hard, <strong>so</strong> I passed the exam.</em> (clear cause → result)</p>
<p>In IELTS, linking words are tested in the <strong>Coherence & Cohesion</strong> criterion for Writing and the <strong>Fluency & Coherence</strong> criterion for Speaking. Even at Band 5–6, you are expected to use basic linking words correctly.</p>`,
      points: [
        'Linking words show the RELATIONSHIP between ideas: addition, contrast, cause, result, time, choice',
        'They make your writing easier to follow and your speaking smoother',
        'You don\'t need complex connectors at first — mastering basic ones is the priority'
      ]
    },
    {
      title: 'Common Linking Words',
      content: `<p>Here are the 10 most important basic linking words, grouped by their function.</p>
<table>
  <thead><tr><th>Linking word</th><th>Function</th><th>Example</th></tr></thead>
  <tbody>
    <tr><td>and</td><td>Addition (adds information)</td><td>I like reading <strong>and</strong> writing.</td></tr>
    <tr><td>also</td><td>Addition (adds a point)</td><td>She speaks French. She <strong>also</strong> speaks Spanish.</td></tr>
    <tr><td>but</td><td>Contrast (shows difference)</td><td>The test was hard, <strong>but</strong> I passed.</td></tr>
    <tr><td>or</td><td>Choice (gives an alternative)</td><td>You can study at home <strong>or</strong> in the library.</td></tr>
    <tr><td>so</td><td>Result (shows what happened)</td><td>It was raining, <strong>so</strong> we stayed inside.</td></tr>
    <tr><td>because</td><td>Reason (explains why)</td><td>I stayed home <strong>because</strong> I was sick.</td></tr>
    <tr><td>then</td><td>Sequence (shows order)</td><td>First read the question, <strong>then</strong> find the answer.</td></tr>
    <tr><td>first / next / finally</td><td>Sequence (lists steps)</td><td><strong>First</strong>, I wake up. <strong>Next</strong>, I have breakfast. <strong>Finally</strong>, I go to school.</td></tr>
    <tr><td>for example</td><td>Example (illustrates a point)</td><td>Many fruits are healthy — <strong>for example</strong>, apples and bananas.</td></tr>
    <tr><td>however</td><td>Contrast (more formal "but")</td><td>The hotel was expensive. <strong>However</strong>, the location was excellent.</td></tr>
  </tbody>
</table>`,
      points: [
        'Start by mastering "and", "but", "so", "because" — these cover most basic connections',
        '"First / next / finally" are very useful for IELTS Writing Task 1 (describing a process)',
        '"However" is your first step toward more formal connectors — use it in Writing Task 2'
      ]
    },
    {
      title: 'Making Compound Sentences',
      content: `<p>A <strong>compound sentence</strong> joins two simple sentences (independent clauses) with a linking word. This is one of the easiest ways to improve your IELTS Writing score.</p>
<table>
  <thead><tr><th>Simple sentences</th><th>Compound sentence</th><th>Linker used</th></tr></thead>
  <tbody>
    <tr><td>I like coffee. I don't like tea.</td><td>I like coffee, <strong>but</strong> I don't like tea.</td><td>but (contrast)</td></tr>
    <tr><td>She studied hard. She got a high score.</td><td>She studied hard, <strong>so</strong> she got a high score.</td><td>so (result)</td></tr>
    <tr><td>It was cold. We went outside.</td><td>It was cold, <strong>but</strong> we went outside.</td><td>but (contrast)</td></tr>
    <tr><td>He works in the morning. He studies in the evening.</td><td>He works in the morning <strong>and</strong> studies in the evening.</td><td>and (addition)</td></tr>
  </tbody>
</table>
<p><strong>Pattern:</strong> Sentence 1 <strong>+ comma + linking word +</strong> Sentence 2</p>
<p><em>Note:</em> When using "and" and the subject is the same, you can drop the second subject: "He works <strong>and</strong> studies." (no comma needed)</p>`,
      points: [
        'Always put a comma before "but", "so", and "or" when joining two full sentences',
        'You can skip the comma before "and" if the subject is the same in both parts',
        'Compound sentences show the examiner you can link ideas — essential for Band 5+'
      ]
    },
    {
      title: 'Avoiding Common Mistakes',
      content: `<p>Beginning IELTS learners often make these linking word errors. Learn to spot and fix them.</p>
<table>
  <thead><tr><th>Mistake</th><th>Wrong example</th><th>Corrected</th></tr></thead>
  <tbody>
    <tr><td><strong>Fragment</strong> (incomplete sentence)</td><td><em>Because I was tired.</em> (no main clause)</td><td><em>I went to bed early <strong>because</strong> I was tired.</em></td></tr>
    <tr><td><strong>Run-on</strong> (two sentences with no linker)</td><td><em>I was tired I went to bed early.</em></td><td><em>I was tired, <strong>so</strong> I went to bed early.</em></td></tr>
    <tr><td><strong>Comma splice</strong> (comma but no linker)</td><td><em>I was tired, I went to bed early.</em></td><td><em>I was tired, <strong>so</strong> I went to bed early.</em></td></tr>
    <tr><td><strong>Double linking</strong> (two linkers for one connection)</td><td><em><strong>Although</strong> I was tired, <strong>but</strong> I kept studying.</em></td><td><em><strong>Although</strong> I was tired, I kept studying.</em> OR <em>I was tired, <strong>but</strong> I kept studying.</em></td></tr>
    <tr><td><strong>Wrong linker for the meaning</strong></td><td><em>I like English <strong>because</strong> I like Maths.</em> (no cause-effect)</td><td><em>I like English <strong>and</strong> I like Maths.</em></td></tr>
  </tbody>
</table>`,
      points: [
        '"Because" and "although" start a dependent clause — they need a main clause to form a complete sentence',
        'Never use "although" and "but" in the same sentence — choose one',
        'Run-on sentences and fragments are major errors in IELTS Writing — they lower your Grammar score'
      ]
    }
  ],

  exercises: [
    {
      title: 'Choose the Correct Linking Word',
      type: 'select',
      instructions: 'Select the linking word that best completes each sentence.',
      questions: [
        {
          text: 'I wanted to go to the park, _______ it was raining.',
          options: ['and', 'but', 'so', 'because'],
          answer: 'but',
          explanation: 'There is a contrast between wanting to go and the rain preventing it. "But" shows contrast.'
        },
        {
          text: 'She studied every day _______ she wanted to pass the exam.',
          options: ['but', 'or', 'because', 'so'],
          answer: 'because',
          explanation: 'Wanting to pass explains WHY she studied. "Because" gives a reason.'
        },
        {
          text: 'You can take the bus _______ walk to school.',
          options: ['but', 'or', 'so', 'because'],
          answer: 'or',
          explanation: '"Or" presents a choice between two alternatives: taking the bus or walking.'
        },
        {
          text: 'The food was delicious, _______ we ordered more.',
          options: ['but', 'because', 'or', 'so'],
          answer: 'so',
          explanation: 'Ordering more is the RESULT of the food being delicious. "So" shows result.'
        }
      ]
    },
    {
      title: 'Complete the Sentences',
      type: 'fill-in',
      instructions: 'Fill in the blank with the correct basic linking word.',
      questions: [
        {
          text: '_______, peel the potatoes. Next, cut them into pieces. Finally, boil them for 20 minutes.',
          answer: 'First',
          explanation: '"First" begins a sequence of steps. It pairs with "next" and "finally" to show order.'
        },
        {
          text: 'I enjoy swimming _______ playing tennis in my free time.',
          answer: 'and',
          explanation: '"And" adds two similar activities together. Both are things the person enjoys.'
        },
        {
          text: 'The train was late, _______ I missed my meeting.',
          answer: 'so',
          explanation: 'Missing the meeting was the RESULT of the train being late. "So" shows the consequence.'
        },
        {
          text: 'He speaks English well. He _______ speaks German fluently.',
          answer: 'also',
          explanation: '"Also" adds extra information — he speaks German in addition to English.'
        }
      ]
    },
    {
      title: 'Fix the Sentence Error',
      type: 'select',
      instructions: 'Choose the correct version of each sentence.',
      questions: [
        {
          text: 'Which sentence is correct?',
          options: [
            'Because I was hungry.',
            'I ate lunch because I was hungry.',
            'Because I was hungry, but I ate lunch.',
            'I was hungry because so I ate lunch.'
          ],
          answer: 'I ate lunch because I was hungry.',
          explanation: '"Because" starts a dependent clause that needs a main clause. "Because I was hungry" alone is a fragment.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'Although the test was difficult, but I passed.',
            'Although the test was difficult, I passed.',
            'Although the test was difficult. I passed.',
            'The test was difficult although but I passed.'
          ],
          answer: 'Although the test was difficult, I passed.',
          explanation: '"Although" and "but" serve the same contrast function — using both is a double-linking error. Use only one.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'She was tired she went to bed.',
            'She was tired, she went to bed.',
            'She was tired, so she went to bed.',
            'She was tired. because she went to bed.'
          ],
          answer: 'She was tired, so she went to bed.',
          explanation: 'Two complete sentences need a linking word. Without one, it is a run-on sentence. "So" shows the result.'
        },
        {
          text: 'Which sentence is correct?',
          options: [
            'I like maths, and I like science.',
            'I like maths and, I like science.',
            'I like maths, and, I like science.',
            'I like maths. And, I like science.'
          ],
          answer: 'I like maths, and I like science.',
          explanation: 'Use a comma before "and" when joining two complete sentences. No comma after "and".'
        }
      ]
    },
    {
      title: 'Write Compound Sentences',
      type: 'writing',
      instructions: 'Join each pair of simple sentences into one compound sentence using an appropriate linking word.',
      questions: [
        {
          text: 'Join: "The weather was bad." + "We enjoyed the trip." (Use a contrast linker)',
          example: 'The weather was bad, but we enjoyed the trip.',
          tip: 'Use "but" to show contrast between the bad weather and the enjoyable trip. Remember the comma before "but".'
        },
        {
          text: 'Join: "He saved money for two years." + "He bought a car." (Use a result linker)',
          example: 'He saved money for two years, so he bought a car.',
          tip: 'Use "so" to show that buying the car was the result of saving money. Remember the comma before "so".'
        },
        {
          text: 'Join: "I wake up early." + "I exercise." + "I have breakfast." (Use sequence linkers)',
          example: 'First, I wake up early. Then, I exercise. Finally, I have breakfast.',
          tip: 'Use "first", "then" (or "next"), and "finally" to show the order of events. Each can start a new sentence.'
        }
      ]
    }
  ],

  tips: [
    'Master "and", "but", "so", and "because" first — they cover 80% of basic linking needs',
    'Always check: does your linking word match the RELATIONSHIP between the ideas? (addition, contrast, cause, result)',
    'Put a comma before "but", "so", and "or" when joining two complete sentences',
    'Never use "although" and "but" together in the same sentence — pick one'
  ],
  dailyPractice: [
    'Write 5 compound sentences each day, using a different linking word in each',
    'Read simple English texts and circle every linking word — identify its function',
    'Take 3 pairs of simple sentences and join them with appropriate linking words',
    'Record yourself speaking for 1 minute on any topic — count how many linking words you use naturally'
  ],
  backPath: '/exams/english/#vocabulary',
  backLabel: 'Back to Vocabulary',
  aiContext: 'Basic Linking Words: Essential beginner-level connectors (and, but, so, because, or, then, also, first/next/finally) for IELTS. Covers forming compound sentences, comma placement, and avoiding common mistakes (fragments, run-ons, comma splices, double linking). Difficulty 1 (beginner). Exercises include selecting correct linkers, sentence completion, error correction, and compound sentence writing.'
};

export function getDataForDifficulty(difficulty = 1) {
  if (difficulty <= 2) {
    return {
      ...linkingWordsBasicData,
      title: linkingWordsBasicData.title + ' (Foundations)',
      overview: { ...linkingWordsBasicData.overview, duration: '15-20 minutes' },
      sections: linkingWordsBasicData.sections.slice(0, Math.max(2, Math.ceil(linkingWordsBasicData.sections.length / 2))),
      exercises: linkingWordsBasicData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...linkingWordsBasicData,
      title: linkingWordsBasicData.title + ' (Advanced)',
      overview: { ...linkingWordsBasicData.overview, duration: '30-35 minutes' },
      sections: linkingWordsBasicData.sections,
      exercises: linkingWordsBasicData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(linkingWordsBasicData.tips || []),
        'For Band 8+: Use less common vocabulary naturally in context.',
        'Advanced tip: Focus on collocations and word families, not just individual words.',
      ],
    };
  }
  return linkingWordsBasicData;
}
